// Port of ios/Sources/ViewModels/EmojiShortcodes.swift.
//
// Where a message says `:tada:` and means 🎉 (shared/API.md §14.5).
//
// The wire speaks NAMES. Everything typed on the desktop arrives as `:name:` in the message
// body, and until this existed the app drew those colons literally — a reader on a phone saw
// `:white_check_mark:` where a reader on the desktop saw a tick.
//
// Pure and offset-exact, because two different consumers need different things from the same
// scan: a standard name is SUBSTITUTED for its character before anything else parses the
// string, while a custom server emoji stays as its `:name:` text and is replaced at layout time
// by an image occupying exactly that many characters. Both need to agree on what a shortcode
// is, and neither may invent one where a message meant a colon.
//
// The delimiter rules are the webapp's, and each exists because of a real string:
// - the opening `:` may not follow a word character, so `https://host` and `12:30:45` are not
//   two shortcodes and a bare URL survives intact;
// - the closing `:` may not be followed by a word character, so `:00:15` stays a duration;
// - the name may not be empty, so `::` is punctuation.
//
// WEB: scanned on Swift Characters (swiftCharacters.ts); offsets are UTF-16 code units.

import { EmojiCharacters } from './emojiCharacters';
import { characterView, isLetter, isNumber } from './swiftCharacters';

/**
 * One `:name:` occurrence. `start` is the opening colon, `end` one past the closing one —
 * UTF-16 offsets (Swift: Character offsets), the same coordinate space as `InlineMarkdown`'s
 * spans.
 */
export interface EmojiShortcodeSpan {
  start: number;
  end: number;
  name: string;
}

/** Mattermost rejects longer names on upload; a runaway scan past this is not a shortcode. */
const maxNameLength = 64;

/** Every shortcode in `text`, left to right, non-overlapping. */
function spans(text: string): EmojiShortcodeSpan[] {
  // No colon, no shortcode — and this is the overwhelmingly common case. The guard exists
  // for the cost of what follows, not for correctness: materialising every Character of every
  // message, once per message per render pass on a list that redraws whenever anything in
  // the conversation changes. Skipping it for the ~95% of messages with no colon in them is
  // the single cheapest thing the message pipeline does (2026-09-08 Mac performance pass).
  // (Swift also returns early under 3 Characters — ":x:" is the shortest possible shortcode;
  // under 3 UTF-16 units is the same cheap bound, and the scan below rejects the rest.)
  if (text.length < 3 || !text.includes(':')) return [];
  const { chars, offsets } = characterView(text);
  const found: EmojiShortcodeSpan[] = [];
  let index = 0;
  while (index < chars.length) {
    if (chars[index] !== ':') {
      index += 1;
      continue;
    }
    if (index > 0 && isWordCharacter(chars[index - 1])) {
      index += 1;
      continue;
    }
    let cursor = index + 1;
    while (cursor < chars.length && isNameCharacter(chars[cursor])) cursor += 1;
    const nameLength = cursor - index - 1;
    const closes = cursor < chars.length && chars[cursor] === ':';
    const followedByWord = cursor + 1 < chars.length && isWordCharacter(chars[cursor + 1]);
    if (nameLength >= 1 && nameLength <= maxNameLength && closes && !followedByWord) {
      found.push({
        start: offsets[index],
        end: offsets[cursor + 1],
        name: chars.slice(index + 1, cursor).join(''),
      });
      // Resume ON the closing colon's successor: `:a::b:` is two shortcodes, and the
      // colon that closed the first is allowed to open the second.
      index = cursor + 1;
    } else {
      index += 1;
    }
  }
  return found;
}

/**
 * `text` with every shortcode `resolve` has an answer for replaced by that answer, and
 * every other one left exactly as typed. A name this client does not know must survive as
 * `:name:`: it is very often a custom server emoji, which a later pass draws as an image,
 * and dropping it here would leave a message with a hole in it.
 */
function substitute(text: string, resolve: (name: string) => string | null | undefined): string {
  const found = spans(text);
  if (found.length === 0) return text;
  let builder = '';
  let cursor = 0;
  let replacedAny = false;
  for (const span of found) {
    const replacement = resolve(span.name);
    if (replacement == null) continue;
    builder += text.slice(cursor, span.start);
    builder += replacement;
    cursor = span.end;
    replacedAny = true;
  }
  if (!replacedAny) return text;
  builder += text.slice(cursor);
  return builder;
}

/**
 * The standard substitution every renderer runs first (`MessageAttributedText`,
 * `MessageBody.embeddedImageSpans`): `substitute` against the generated `EmojiCharacters`.
 */
function substituteStandard(text: string): string {
  return substitute(text, EmojiCharacters.character);
}

/**
 * The shortcodes in `text` whose names are in `names` — the custom-emoji pass, run AFTER
 * the standard substitution so it only ever sees what is left.
 */
function spansMatching(text: string, names: ReadonlySet<string>): EmojiShortcodeSpan[] {
  if (names.size === 0) return [];
  return spans(text).filter((s) => names.has(s.name));
}

function isWordCharacter(character: string): boolean {
  return isLetter(character) || isNumber(character) || character === '_';
}

/**
 * Mattermost emoji names are `[a-z0-9_+-]`. Upper-case letters are scanned as name
 * characters so `:Tada:` is recognised as a shortcode and then simply fails to resolve —
 * stopping the scan at the capital instead would split the token and could turn the rest
 * of the line into a different shortcode.
 */
function isNameCharacter(character: string): boolean {
  return isLetter(character) || isNumber(character) || character === '_' || character === '+' || character === '-';
}

export const EmojiShortcodes = {
  spans,
  substitute,
  substituteStandard,
  spansMatching,
} as const;
